/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/response/TireSearchResponse';
import { RootState } from 'app/AppStore';
import { ChevronDownIcon, CopyTireIcon, TiresNoReplacementFoundIcon } from 'assets/icons';
import { DataContainer } from 'components/DataContainer';
import { SortData } from 'components/Sort/Sort.types';
import { getMyStoreTires } from 'domains/myStore/MyStore.store';
import { getPricesMap, getStocksMapV2 } from 'domains/references';
import { getSortedTires } from 'domains/tires/Tire.mapper';
import { TireSortReference } from 'domains/tires/Tire.types';
import { SparePartsViewType } from 'domains/user';
import { OpennerWrapper } from 'pages/CatalogPage/IAM/FullTextSearchResult/SearchResultSection.styled';
import { TireReferenceCardsInfiniteScroll } from 'pages/TiresPage/TireReferencesSection/TireReferencesContainer/TireReferenceCardsInfiniteScroll';
import { theme } from 'styles';
import { Box, Flex, Icon, MarginBox, PaddingBox, Spin, Text } from 'UI';
import { FOUND, getSearchData, isLoading, LOADING, NOT_FOUND, SEARCH_STATUS } from 'utils';

const TireStocksLoader = ({ referencesCount }: { referencesCount: number }) => {
  const { t } = useTranslation();
  return (
    <Box height={100} align={'center'}>
      <Flex direction={'row'} justify={'center'} align={'center'}>
        <Text type={'h3_dim'} transform={'capitalize'}>
          {t('catalog.references_list.loading', 'Loading data for {{count}} references', {
            count: referencesCount,
          })}
        </Text>
        <MarginBox mr={15} />
        <Spin />
      </Flex>
    </Box>
  );
};

interface TireReferencesProps {
  tires: TireItem[];
  sparePartsView: SparePartsViewType;
  singleSection: boolean;
  title?: string;
  initShowCount: number;
  isNoLongerAvailable?: boolean;
  isAvailableSoon?: boolean;
  compact?: boolean;
  open?: boolean;
  sort?: SortData<TireSortReference>[];
}

export const TireReferenceCardsContainer = ({
  tires,
  sparePartsView,
  singleSection,
  title,
  initShowCount,
  isNoLongerAvailable,
  isAvailableSoon,
  compact,
  open,
  sort,
}: TireReferencesProps) => {
  const referenceNumbers = tires.map((item) => item.partNumber);
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, referenceNumbers ?? []));
  const stocksSearchStatus = useMemo(() => {
    return [...[...stocks.values()].map((s) => s?.searchStatus)];
  }, [stocks]);
  const stocksLoading = stocksSearchStatus.some((sp) => isLoading(sp));
  const myStoreTiresDiscounts = getSearchData(useSelector(getMyStoreTires).brands);

  const prices = useSelector((state: RootState) => getPricesMap(state, referenceNumbers ?? []));
  const pricesLoading = useMemo(() => {
    return [...prices.values()].some((price) => isLoading(price));
  }, [prices]);

  const [stockAndPriceDataStatus, setStockAndPriceDataStatus] = useState<SEARCH_STATUS>(LOADING);
  const [allReferences, setAllReferences] = useState<TireItem[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(open ?? true);

  useEffect(() => {
    setIsOpen(open ?? true);
  }, [open]);

  useEffect(() => {
    if (!stocksLoading && !pricesLoading) {
      setStockAndPriceDataStatus(FOUND);
      const sortedTires = getSortedTires(tires, stocks, prices, sparePartsView, myStoreTiresDiscounts, sort);
      if (sortedTires.length === 0) {
        setStockAndPriceDataStatus(NOT_FOUND);
      }
      setAllReferences(sortedTires);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocksLoading, pricesLoading, JSON.stringify(referenceNumbers), sort, sparePartsView, myStoreTiresDiscounts]);

  const displayTitleAndMargin = stockAndPriceDataStatus !== FOUND || (tires.length > 0 && allReferences.length > 0);
  const tireLoader = () => <TireStocksLoader referencesCount={tires.length} />;
  const showFoldIcon = !!(isAvailableSoon || isNoLongerAvailable) && allReferences.length > 0;

  useEffect(() => {
    if (singleSection) {
      setIsOpen(true);
    }
  }, [singleSection]);

  return (
    <Box background={theme.color.white}>
      {title && (
        <MarginBox mx={10}>
          <Flex align={'center'} background={theme.color.white} justify={'space-between'}>
            <PaddingBox pl={15} pb={16} pt={20}>
              {(isNoLongerAvailable || isAvailableSoon) && (
                <Icon IconComponent={CopyTireIcon} mr={10} height={20} width={20} noPointer={true} />
              )}
              <Text type={'h4_paragraph_title'}>{title}</Text>
            </PaddingBox>
            {showFoldIcon && (
              <PaddingBox pl={15} pb={15} pr={10} pt={30}>
                <OpennerWrapper $isOpen={isOpen}>
                  <Icon
                    IconComponent={ChevronDownIcon}
                    color={theme.color.brand_black}
                    size={25}
                    onClick={() => setIsOpen(!isOpen)}
                  />
                </OpennerWrapper>
              </PaddingBox>
            )}
          </Flex>
        </MarginBox>
      )}
      {isOpen && tires.length > 0 && (
        <DataContainer data={stockAndPriceDataStatus} Loading={tireLoader}>
          <TireReferenceCardsInfiniteScroll
            tires={allReferences}
            sparePartsView={sparePartsView}
            singleSection={singleSection}
            initShowCount={initShowCount}
            compact={compact}
          />
        </DataContainer>
      )}
      {isOpen && tires.length === 0 && (
        <Flex justify={'center'}>
          <Icon IconComponent={TiresNoReplacementFoundIcon} height={500} width={500} noPointer={true} />
        </Flex>
      )}
      {displayTitleAndMargin}
    </Box>
  );
};
